import { ListboxButton } from '@headlessui/react'
import useTranslations from '@translations/useTranslations'
import Spinner from '@ui-components/Loaders/Spinner'
import type { Placement } from '@ui-components/Tooltip'
import classnames from 'classnames'
import MultiselectButtonContent from '../ButtonContent/MultiselectButtonContent'
import type { IMultiselectProps } from '../Multiselect'

interface IMultiselectButton extends IMultiselectProps {
  open: boolean
  popperPlacement: Placement | null
  setButtonElement: Function
}

const theme = {
  button: {
    shared:
      'relative w-100 rounded-md border pt-2 pl-3 pr-4 data-[open]:border-electric-violet-500',
    default: 'border-oxford-gray-200',
    error: 'border-watermelon-800',
    disabled: 'bg-oxford-gray-50 border-[#E5E7EB]'
  }
}

const MultiselectButton = ({
  id = '',
  name = '',
  className,
  hideButton,
  value,
  data,
  placeholder,
  textField = 'name',
  valueField = 'id',
  title,
  open,
  disabled,
  setButtonElement,
  error,
  loading,
  displayedItemsCount,
  popperPlacement,
  renderTooltipItem,
  isItemRemovable,
  renderContent,
  onChange,
  onBlur
}: IMultiselectButton) => {
  const { t } = useTranslations()

  if (!placeholder) {
    placeholder = t('expressions.select')
  }

  const contentProps = {
    value,
    error,
    disabled,
    textField,
    valueField,
    open,
    data,
    title,
    placeholder,
    displayedItemsCount,
    onChange,
    renderTooltipItem,
    isItemRemovable
  }

  return (
    <ListboxButton
      ref={(newRef) => {
        if (newRef) {
          setButtonElement((ref) => {
            if (ref) {
              return ref
            }

            return newRef
          })
        }
      }}
      onBlur={onBlur}
      className={
        !hideButton
          ? classnames([
              theme.button.shared,
              {
                [theme.button.default]: !error && !disabled,
                [theme.button.error]: !!error && !disabled,
                [theme.button.disabled]: disabled,
                'data-[open]:rounded-b-none':
                  popperPlacement && popperPlacement !== 'top',
                'data-[open]:rounded-t-none':
                  popperPlacement && popperPlacement === 'top'
              },
              className
            ])
          : ''
      }
      name={name}
      id={id}
    >
      {!hideButton && (
        <>
          <div className="flex flex-auto flex-wrap">
            {typeof renderContent === 'function' ? (
              renderContent(contentProps)
            ) : (
              <MultiselectButtonContent {...contentProps} />
            )}
          </div>

          <span className="absolute inset-y-0 right-0 px-3 flex items-center pointer-events-none">
            {loading ? (
              <Spinner />
            ) : (
              <i
                className={classnames([
                  'fas fa-caret-down text-sm',
                  {
                    'text-oxford-gray-800': !disabled,
                    'text-oxford-gray-200': disabled
                  }
                ])}
                aria-hidden="true"
              />
            )}
          </span>
        </>
      )}
    </ListboxButton>
  )
}

export default MultiselectButton
